import { useEffect, useRef, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm, usePrompt } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";

type Props = {
  invoiceId: string;
  market: string | null;
  onReload: () => void;
};

type Busy =
  | null
  | "complete"
  | "reassign"
  | "regenerate"
  | "conciliate"
  | "status";

const todayISO = (): string => new Date().toISOString().slice(0, 10);

export default function InvoiceActionsMenu({
  invoiceId,
  market,
  onReload,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [feedback, setFeedback] = useState<
    | { kind: "success" | "error" | "info"; message: string }
    | null
  >(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const post = async (
    path: string,
    body: Record<string, unknown> | undefined,
    busyKey: Busy,
    successMsg: string,
    refresh = true,
  ) => {
    if (busy) return;
    setBusy(busyKey);
    setFeedback(null);
    try {
      const data = await request<Record<string, unknown>>(path, {
        method: "POST",
        token,
        body: body ?? {},
      });
      setFeedback({ kind: "success", message: successMsg });
      if (refresh) onReload();
      return data;
    } catch (err) {
      setFeedback({
        kind: "error",
        message: err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200)
            }`
          : "Acción falló",
      });
    } finally {
      setBusy(null);
    }
  };

  const handleComplete = async () => {
    const date = await prompt({
      title: "Marcar factura como pagada",
      message: `Factura ${invoiceId}. Introduce la fecha de pago.`,
      defaultValue: todayISO(),
      type: "date",
      okLabel: "Marcar como pagada",
      validate: (v) =>
        /^\d{4}-\d{2}-\d{2}$/.test(v) ? null : "Formato esperado: YYYY-MM-DD",
    });
    if (!date) return;
    await post(
      `/api/admin/operations/invoices/${invoiceId}/complete`,
      { completionDate: date },
      "complete",
      "Pago completado",
    );
  };

  const handleRegenerate = async () => {
    const ok = await confirm({
      title: "Regenerar factura Stripe",
      message: "¿Regenerar nueva factura en Stripe? Esto creará un nuevo charge.",
      confirmLabel: "Regenerar",
      tone: "warning",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/invoices/${invoiceId}/regenerate-stripe`,
      undefined,
      "regenerate",
      "Nueva factura Stripe generada",
    );
  };

  const handleReassign = async () => {
    const stripeId = await prompt({
      title: "Reasignar Stripe invoice",
      message: `Factura ${invoiceId}. Introduce el Stripe invoice ID al que reasignarla.`,
      placeholder: "in_...",
      okLabel: "Reasignar",
      tone: "warning",
      validate: (v) => (v.startsWith("in_") ? null : "Debe empezar por 'in_'"),
    });
    if (!stripeId) return;
    await post(
      `/api/admin/operations/invoices/${invoiceId}/reassign-stripe`,
      { stripeInvoiceId: stripeId },
      "reassign",
      "Stripe invoice reasignado",
    );
  };

  const conciliateAllowed = market === "IT" || market === "SQL_ES";

  const handleConciliate = async () => {
    if (!conciliateAllowed) return;
    const ok = await confirm({
      title: "Enviar a conciliación",
      message: `Enviar la factura a conciliación AGL/SquareLife (market ${market}).`,
      confirmLabel: "Enviar a conciliación",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/invoices/${invoiceId}/conciliate`,
      undefined,
      "conciliate",
      "Enviada a conciliación",
    );
  };

  const handleCheckStatus = async () => {
    if (busy) return;
    setBusy("status");
    setFeedback(null);
    try {
      const data = await request<{ invoiceId: string; status: unknown }>(
        `/api/admin/operations/invoices/${invoiceId}/conciliation-status`,
        { token },
      );
      setFeedback({
        kind: "info",
        message: `Status: ${typeof data.status === "string" ? data.status : JSON.stringify(data.status)}`,
      });
    } catch (err) {
      setFeedback({
        kind: "error",
        message: err instanceof HttpError ? `HTTP ${err.status}` : "Falló",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Acciones de factura"
        style={{
          padding: "0.3rem 0.6rem",
          borderRadius: "0.35rem",
          background: open ? "#0069d9" : "white",
          color: open ? "white" : "#1f2933",
          border: "1px solid #dee2e6",
          fontSize: ".75rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        ⋯
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 20,
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.5rem",
            boxShadow: "0 6px 16px rgba(0,0,0,.12)",
            minWidth: 240,
            padding: "0.35rem",
          }}
        >
          <MenuItem
            onClick={() => {
              setOpen(false);
              handleComplete();
            }}
            disabled={busy === "complete"}
          >
            Marcar como pagada…
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false);
              handleRegenerate();
            }}
            disabled={busy === "regenerate"}
          >
            Regenerar factura Stripe
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false);
              handleReassign();
            }}
            disabled={busy === "reassign"}
          >
            Reasignar Stripe invoice…
          </MenuItem>
          {conciliateAllowed && (
            <>
              <div
                style={{ borderTop: "1px solid #f1f3f5", margin: "0.25rem 0" }}
              />
              <MenuItem
                onClick={() => {
                  setOpen(false);
                  handleConciliate();
                }}
                disabled={busy === "conciliate"}
              >
                Enviar a conciliación
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpen(false);
                  handleCheckStatus();
                }}
                disabled={busy === "status"}
              >
                Ver status conciliación
              </MenuItem>
            </>
          )}
        </div>
      )}
      {feedback && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 25,
            background:
              feedback.kind === "success"
                ? "#d4edda"
                : feedback.kind === "info"
                  ? "#e7f1ff"
                  : "#fde2e2",
            color:
              feedback.kind === "success"
                ? "#155724"
                : feedback.kind === "info"
                  ? "#0a58ca"
                  : "#b32424",
            padding: "0.4rem 0.65rem",
            borderRadius: "0.4rem",
            fontSize: ".75rem",
            maxWidth: 280,
            boxShadow: "0 4px 10px rgba(0,0,0,.1)",
            cursor: "pointer",
          }}
          onClick={() => setFeedback(null)}
          title="Cerrar"
        >
          {feedback.message}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  onClick,
  disabled,
  hint,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={hint}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "0.4rem 0.55rem",
        borderRadius: "0.35rem",
        background: "transparent",
        color: disabled ? "#adb5bd" : "#1f2933",
        border: "none",
        fontSize: ".82rem",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "#f8f9fb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children}
    </button>
  );
}
